import { safeLocalStorage } from "@/utils/localStorageUtils";

const DEVICE_ID_KEY = "device_id";
const LEGACY_DEVICE_TOKEN_KEY = "deviceToken";
const UUID_TEMPLATE = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx";
const MOBILE_USER_AGENT_PATTERN =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

export const DEVICE_ID_STORAGE_KEY = "dispatchiq_device_id";

export type AuthDeviceInfo = {
    userAgent: string;
    deviceToken: string;
    platform: "web";
    modelName: string;
    lastLoginIPAddress: string;
    isMobileApp: boolean;
    allowPush: boolean;
};

const hasWindow = (): boolean => typeof window !== "undefined";

const normalizeDeviceId = (value: unknown): string | null => {
    if (typeof value !== "string") {
        return null;
    }

    const normalizedValue = value.trim();
    return normalizedValue.length > 0 ? normalizedValue : null;
};

const formatUuid = (bytes: Uint8Array): string => {
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"));

    return [
        hex.slice(0, 4).join(""),
        hex.slice(4, 6).join(""),
        hex.slice(6, 8).join(""),
        hex.slice(8, 10).join(""),
        hex.slice(10, 16).join(""),
    ].join("-");
};

const generateUuidLikeDeviceId = (): string => {
    const randomUuid = globalThis.crypto?.randomUUID?.();
    if (randomUuid) {
        return randomUuid;
    }

    const randomBytes = new Uint8Array(16);
    if (globalThis.crypto?.getRandomValues) {
        globalThis.crypto.getRandomValues(randomBytes);
        randomBytes[6] = (randomBytes[6] & 0x0f) | 0x40;
        randomBytes[8] = (randomBytes[8] & 0x3f) | 0x80;
        return formatUuid(randomBytes);
    }

    return UUID_TEMPLATE.replace(/[xy]/g, (placeholder) => {
        const randomNibble = Math.floor(Math.random() * 16);
        const value =
            placeholder === "x" ? randomNibble : (randomNibble & 0x3) | 0x8;
        return value.toString(16);
    });
};

const readLegacyDeviceId = (): string | null => {
    if (!hasWindow()) {
        return null;
    }

    try {
        return normalizeDeviceId(
            window.localStorage.getItem(LEGACY_DEVICE_TOKEN_KEY)
        );
    } catch {
        return null;
    }
};

const persistDeviceId = (deviceId: string): string => {
    const normalizedDeviceId = normalizeDeviceId(deviceId);
    if (!normalizedDeviceId) {
        return generateUuidLikeDeviceId();
    }

    safeLocalStorage.setItem(DEVICE_ID_KEY, normalizedDeviceId);

    if (hasWindow()) {
        try {
            window.localStorage.removeItem(LEGACY_DEVICE_TOKEN_KEY);
        } catch {
            // Ignore localStorage failures in restricted environments.
        }
    }

    return normalizedDeviceId;
};

export function getStoredDeviceId(): string | null {
    const storedDeviceId = normalizeDeviceId(
        safeLocalStorage.getItem<string>(DEVICE_ID_KEY)
    );
    if (storedDeviceId) {
        return storedDeviceId;
    }

    const legacyDeviceId = readLegacyDeviceId();
    if (!legacyDeviceId) {
        return null;
    }

    return persistDeviceId(legacyDeviceId);
}

export function getOrCreateDeviceId(): string {
    const storedDeviceId = getStoredDeviceId();
    if (storedDeviceId) {
        return storedDeviceId;
    }

    return persistDeviceId(generateUuidLikeDeviceId());
}

export function clearStoredDeviceId(): void {
    safeLocalStorage.removeItem(DEVICE_ID_KEY);

    if (!hasWindow()) {
        return;
    }

    try {
        window.localStorage.removeItem(LEGACY_DEVICE_TOKEN_KEY);
    } catch {
        // Ignore localStorage failures in restricted environments.
    }
}

export function resetStoredDeviceId(): string {
    clearStoredDeviceId();
    return getOrCreateDeviceId();
}

export function clearLocalStoragePreservingDeviceId(): void {
    if (!hasWindow()) {
        return;
    }

    const storedDeviceId = getStoredDeviceId();
    window.localStorage.clear();

    if (storedDeviceId) {
        safeLocalStorage.setItem(DEVICE_ID_KEY, storedDeviceId);
    }
}

export function buildAuthDeviceInfo(): AuthDeviceInfo {
    const userAgent =
        typeof navigator !== "undefined" ? navigator.userAgent : "Unknown";

    return {
        userAgent,
        deviceToken: getOrCreateDeviceId(),
        platform: "web",
        modelName:
            typeof navigator !== "undefined"
                ? navigator.platform || "Unknown"
                : "Unknown",
        lastLoginIPAddress: "0.0.0.0",
        isMobileApp:
            typeof navigator !== "undefined"
                ? MOBILE_USER_AGENT_PATTERN.test(userAgent)
                : false,
        allowPush: true,
    };
}
